import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { pollCheckoutStatus } from "../game/authService";
import { useAuth } from "../contexts/AuthContext";

// Stripe returns the user to /vip/success?session_id=...
// We poll our backend (which polls Stripe) until status resolves.
// Idempotent — backend only applies the VIP upgrade once per session_id.
export default function VipSuccess({ onContinue }) {
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "pending", attempts: 0 });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) {
      setState({ status: "error", attempts: 0 });
      return;
    }
    let cancelled = false;
    let attempt = 0;
    const tick = async () => {
      if (cancelled) return;
      attempt += 1;
      try {
        const r = await pollCheckoutStatus(sid);
        if (cancelled) return;
        if (r.payment_status === "paid") {
          await refresh();
          setState({
            status: "paid",
            attempts: attempt,
            planType: r.plan_type,
            planName: r.plan_name,
            planCoins: r.plan_coins,
            planCoinsBonus: r.plan_coins_bonus,
          });
          return;
        }
        if (r.status === "expired") {
          setState({ status: "expired", attempts: attempt });
          return;
        }
        if (attempt >= 12) {
          setState({ status: "timeout", attempts: attempt });
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attempt >= 5) {
          setState({ status: "error", attempts: attempt });
          return;
        }
        setTimeout(tick, 2000);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const { status, planType, planName, planCoins, planCoinsBonus } = state;
  return (
    <div className="screen vip-success-screen" data-testid="vip-success">
      <div className="vip-success-card">
        {status === "pending" && (
          <>
            <Loader2 size={48} className="spin" />
            <h2>Confirming payment…</h2>
            <p>Hold tight — this usually takes a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} color="#7ed957" />
            {planType === "coins" ? (
              <>
                <h2>{(planCoins || 0).toLocaleString()} coins added! 🪙</h2>
                <p>{planName || "Coin pack"} unlocked. Spend them in the Cosmetics shop.</p>
              </>
            ) : (
              <>
                <h2>You're in! 🎉</h2>
                <p>VIP unlocked. Enjoy 4 choices, free rerolls, and the daily crate.</p>
                {(planCoinsBonus || 0) > 0 && (
                  <p className="vip-success-bonus" data-testid="vip-success-coin-bonus">
                    Bonus: +{planCoinsBonus.toLocaleString()} coins added to your wallet!
                  </p>
                )}
              </>
            )}
            <button
              type="button"
              className="zc-btn primary"
              onClick={onContinue}
              data-testid="vip-success-continue"
            >
              Back to game
            </button>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <XCircle size={48} color="#ff7676" />
            <h2>Something didn't click</h2>
            <p>
              {status === "expired"
                ? "The checkout session expired."
                : "We couldn't confirm your payment. If money was charged, refresh in a minute and try again."}
            </p>
            <button type="button" className="zc-btn" onClick={onContinue}>
              Back to game
            </button>
          </>
        )}
      </div>
    </div>
  );
}
